import React, { useEffect, useState } from "react";
import { api, fmtDate, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Phone, UserCheck } from "lucide-react";
import StatusBadge from "@/components/crm/StatusBadge";
import { SearchBox, DeleteButton, matchesQuery } from "@/components/crm/RecordActions";
import FilterBar, { FilterSelect, inDateRange } from "@/components/crm/Filters";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["New","Contacted","Follow-up","Site Visit","Quotation","Negotiation","Won","Lost"];
const SOURCES = ["Website","Facebook","Instagram","Google Form","Referral","Walk-in","Other"];
const PROPERTY = ["Residential","Commercial","Industrial","Agriculture"];

const empty = { name:"", mobile:"", whatsapp:"", email:"", address:"", city:"", source:"Website", solar_requirement:"", property_type:"Residential", status:"New", follow_up_date:"", notes:"" };

export default function Leads() {
  const { user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [view, setView] = useState("table");
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fSource, setFSource] = useState("__all__");
  const [fAssignee, setFAssignee] = useState("__all__");

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
    if (user?.role === "admin") {
      try { setUsers((await api.get("/users")).data.filter(u => u.role === "sales" || u.role === "admin")); } catch {}
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editId) await api.patch(`/leads/${editId}`, form);
      else await api.post("/leads", form);
      toast.success(editId ? "Lead updated" : "Lead created");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const edit = (l) => { setForm({ ...empty, ...l }); setEditId(l.id); setOpen(true); };

  const changeStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    toast.success(`Moved to ${status}`);
    load();
  };

  const convert = async (id) => {
    try {
      await api.post(`/leads/${id}/convert`);
      toast.success("Converted to customer");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const del = async (l) => {
    try { await api.delete(`/leads/${l.id}`); toast.success(`Lead ${l.lead_no} deleted`); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const visible = leads
    .filter((l) => matchesQuery(l, search))
    .filter((l) => inDateRange(l.created_at, fFrom, fTo))
    .filter((l) => fSource === "__all__" || l.source === fSource)
    .filter((l) => fAssignee === "__all__" || l.assigned_to === fAssignee);
  const filtered = filter === "All" ? visible : visible.filter((l) => l.status === filter);

  return (
    <div className="space-y-5" data-testid="leads-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Leads <span className="text-slate-400 text-lg font-medium">/ लीड्स</span></h1>
          <p className="text-slate-500 text-sm">Track and convert every enquiry.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <SearchBox value={search} onChange={setSearch} placeholder="Search name, mobile, city..." testid="leads-search-input" />
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="table" data-testid="leads-view-table">Table</TabsTrigger>
              <TabsTrigger value="kanban" data-testid="leads-view-kanban">Kanban</TabsTrigger>
            </TabsList>
          </Tabs>
          <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){setForm(empty); setEditId(null);}}}>
            <DialogTrigger asChild>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" data-testid="add-lead-btn">
                <Plus className="w-4 h-4 mr-1"/>New Lead
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{editId ? "Edit Lead" : "New Lead"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Name*"><Input value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="lead-name-input"/></Field>
                <Field label="Mobile*"><Input value={form.mobile} onChange={e=>setForm({...form,mobile:e.target.value})} data-testid="lead-mobile-input"/></Field>
                <Field label="WhatsApp"><Input value={form.whatsapp} onChange={e=>setForm({...form,whatsapp:e.target.value})}/></Field>
                <Field label="Email"><Input value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></Field>
                <Field label="City"><Input value={form.city} onChange={e=>setForm({...form,city:e.target.value})}/></Field>
                <Field label="Address"><Input value={form.address} onChange={e=>setForm({...form,address:e.target.value})}/></Field>
                <Field label="Source">
                  <Select value={form.source} onValueChange={v=>setForm({...form,source:v})}>
                    <SelectTrigger data-testid="lead-source-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{SOURCES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Property Type">
                  <Select value={form.property_type} onValueChange={v=>setForm({...form,property_type:v})}>
                    <SelectTrigger><SelectValue/></SelectTrigger>
                    <SelectContent>{PROPERTY.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Solar Requirement (kW)"><Input value={form.solar_requirement} onChange={e=>setForm({...form,solar_requirement:e.target.value})} placeholder="e.g. 5 kW"/></Field>
                <Field label="Status">
                  <Select value={form.status} onValueChange={v=>setForm({...form,status:v})}>
                    <SelectTrigger data-testid="lead-status-select"><SelectValue/></SelectTrigger>
                    <SelectContent>{STATUSES.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Assign To">
                  <Select value={form.assigned_to || "none"} onValueChange={v=>setForm({...form,assigned_to: v==="none"?null:v})}>
                    <SelectTrigger data-testid="lead-assignee-select"><SelectValue placeholder="Auto"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Auto / Unassigned</SelectItem>
                      {users.map(u=><SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Follow-up Date"><Input type="date" value={form.follow_up_date||""} onChange={e=>setForm({...form,follow_up_date:e.target.value})}/></Field>
                <div className="sm:col-span-2">
                  <Field label="Notes"><Textarea value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} rows={3}/></Field>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
                <Button className="bg-amber-500 hover:bg-amber-600" onClick={save} data-testid="save-lead-btn">Save</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <FilterBar from={fFrom} to={fTo} onFrom={setFFrom} onTo={setFTo}
        onClear={()=>{setFFrom("");setFTo("");setFSource("__all__");setFAssignee("__all__");setSearch("");}}>
        <FilterSelect value={fSource} onChange={setFSource} placeholder="Source" testid="filter-source"
          options={SOURCES.map(s=>({value:s,label:s}))} />
        {user?.role === "admin" && (
          <FilterSelect value={fAssignee} onChange={setFAssignee} placeholder="Assignee" testid="filter-assignee"
            options={users.map(u=>({value:u.id,label:u.name}))} />
        )}
      </FilterBar>

      {view === "table" ? (
        <Card><CardContent className="p-0">
          <div className="p-4 flex flex-wrap gap-2 border-b border-slate-100">
            {["All", ...STATUSES].map(s=>(
              <button key={s} onClick={()=>setFilter(s)}
                className={`px-3 py-1 rounded-full text-xs font-semibold border ${filter===s?"bg-slate-900 text-white border-slate-900":"bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                data-testid={`lead-filter-${s.toLowerCase().replace(/\s+/g,'-')}`}>{s}</button>
            ))}
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Lead #</TableHead><TableHead>Name</TableHead><TableHead>Mobile</TableHead>
              <TableHead>Source</TableHead><TableHead>Requirement</TableHead>
              <TableHead>Status</TableHead><TableHead>Follow-up</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.length===0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">{search ? `No leads matching "${search}"` : "No leads yet. Click \"New Lead\" to add."}</TableCell></TableRow>}
              {filtered.map(l=>(
                <TableRow key={l.id} data-testid={`lead-row-${l.lead_no}`}>
                  <TableCell className="font-mono text-xs">{l.lead_no}</TableCell>
                  <TableCell className="font-medium">{l.name}<div className="text-xs text-slate-500">{l.city}</div></TableCell>
                  <TableCell><a href={`tel:${l.mobile}`} className="text-slate-700 hover:text-amber-600 inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{l.mobile}</a></TableCell>
                  <TableCell><span className="text-xs bg-slate-100 px-2 py-0.5 rounded-full">{l.source}</span></TableCell>
                  <TableCell className="text-xs">{l.solar_requirement||"-"}</TableCell>
                  <TableCell><StatusBadge value={l.status}/></TableCell>
                  <TableCell className="text-xs">{fmtDate(l.follow_up_date)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={()=>edit(l)} data-testid={`edit-lead-${l.lead_no}`}>Edit</Button>
                      {!l.converted && <Button size="sm" variant="outline" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={()=>convert(l.id)} data-testid={`convert-lead-${l.lead_no}`}><UserCheck className="w-3 h-3 mr-1"/>Convert</Button>}
                      {user?.role === "admin" && (
                        <DeleteButton title={`Delete Lead ${l.lead_no}?`} description={`This will permanently delete the lead "${l.name}". This action cannot be undone.`} onConfirm={()=>del(l)} testid={`delete-lead-${l.lead_no}`} />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {STATUSES.map(col=>(
            <Card key={col} className="bg-slate-50/60 border-slate-200">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm text-slate-800">{col}</div>
                  <span className="text-xs bg-white border border-slate-200 rounded-full px-2">{visible.filter(l=>l.status===col).length}</span>
                </div>
                <div className="space-y-2 min-h-[100px]">
                  {visible.filter(l=>l.status===col).map(l=>(
                    <div key={l.id} className="bg-white p-3 rounded-lg border border-slate-200 hover:border-amber-300 hover:shadow-sm transition-all cursor-pointer" onClick={()=>edit(l)} data-testid={`kanban-card-${l.lead_no}`}>
                      <div className="text-xs text-slate-400 font-mono">{l.lead_no}</div>
                      <div className="font-semibold text-slate-900 text-sm">{l.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{l.mobile} · {l.city}</div>
                      <div className="text-xs text-amber-700 font-medium mt-1">{l.solar_requirement}</div>
                      <div className="mt-2 flex gap-1 flex-wrap">
                        {STATUSES.filter(s=>s!==col).slice(0,3).map(s=>(
                          <button key={s} onClick={(e)=>{e.stopPropagation(); changeStatus(l.id,s);}} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 hover:bg-amber-100 text-slate-600">→ {s}</button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs text-slate-600 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
